import {
  AllBooking,
  BookingWithImage,
  GenericConfigType,
  GetAllBookingsMyMscResponse,
  IFetchPortsImagesFromFragmentsResponse,
  ImageConfig,
  PortItem,
  PortsImagesListGraphQlQueryRequest,
} from '@msc-b2c/interfaces/interfaces';
import { ItineraryResponse } from '@msc-b2c/interfaces/types';
import {
  fetchContentFragmentsFromCms,
  getActiveSession,
  GetAllBookingsMyMSC,
  getImageUrl,
  GetItineraryMyMSC,
  pickItImageAvoidingDupl,
} from '@msc-b2c/shared-utils';
import {
  getItineraryImage,
  getPortImagesFromItineraryData,
  enrichImageAltFromPortImages,
} from '../../itinerarycard/ItineraryCard.hooks';

// ---------------------------------------------------------------------------
// Internal helpers shared by all three exported functions
// ---------------------------------------------------------------------------

const createImagePicker = (
  usedImagesMap: Map<string, Set<string>>,
  lastImageRef: { value: string }
) => {
  return (images: ImageConfig[]): ImageConfig => {
    if (!Array.isArray(images) || images.length === 0) return {} as ImageConfig;
    const key = getImageUrl(images[0]);
    if (!key) return images[0];

    if (!usedImagesMap.has(key)) {
      usedImagesMap.set(key, new Set<string>());
    }
    const usedImagesForKey = usedImagesMap.get(key) as Set<string>;
    const result = pickItImageAvoidingDupl(images, usedImagesForKey, lastImageRef.value);
    if (result) {
      lastImageRef.value = result.lastImageUsed;
      return result.selected;
    }
    return images[0];
  };
};

export function needsPortImagesForItinerary(
  itinerary: ItineraryResponse | null | undefined,
  genericConfigs: GenericConfigType
): boolean {
  if (!itinerary) return false;
  const codeImages =
    genericConfigs?.configs?.cruiseSearchConfig?.itineraryImages?.itineraryCodesImagesConfig?.[
      itinerary.code
    ];
  return !codeImages?.length;
}

export function resolveItineraryCardImage(
  itinerary: ItineraryResponse,
  areaCode: string,
  genericConfigs: GenericConfigType,
  portsData: PortItem[] | null | undefined,
  pickImage?: (images: ImageConfig[]) => ImageConfig
): ImageConfig | undefined {
  const portImages =
    portsData != null ? getPortImagesFromItineraryData(portsData, itinerary) : undefined;

  return enrichImageAltFromPortImages(
    getItineraryImage(itinerary.code, areaCode, pickImage, genericConfigs, portImages),
    portsData,
    itinerary
  );
}

/** Departure date sort key extracted from the cruiseId (`<2-char ship><YYYYMMDD>...`). */
const departureSortKey = (cruiseId: string | undefined): number => {
  if (!cruiseId || cruiseId.length < 10) return Number.POSITIVE_INFINITY;
  const yyyymmdd = cruiseId.substring(2, 10);
  const year = Number(yyyymmdd.substring(0, 4));
  const month = Number(yyyymmdd.substring(4, 6));
  const day = Number(yyyymmdd.substring(6, 8));
  if (!year || !month || !day) return Number.POSITIVE_INFINITY;
  return Date.UTC(year, month - 1, day);
};

// ---------------------------------------------------------------------------
// Phase 1 — metadata-only rotation (no API calls)
// ---------------------------------------------------------------------------

/**
 * Computes one image per booking using only data already present in the
 * `/users/{email}/bookings` response (`region`, `cruiseId`) plus the static
 * `genericConfigs.cruiseSearchConfig.itineraryImages` lookups.
 *
 * Bookings are sorted by departure date (encoded in `cruiseId`) so rotation
 * is stable across pages and reloads.
 *
 * Image-source priority: `areaImagesConfig[region]` → `fallbackImagesConfig`.
 * The higher-priority tiers (`itineraryCodesImagesConfig` and port images)
 * require itinerary data that is not available at this stage; they are applied
 * later by `upgradeBookingsImagesWithItineraryCodes` once AllMyCruises has
 * fetched per-booking itineraries.
 *
 * No API calls. Safe to invoke from the login flow before redirecting to
 * Overview or as a fallback when the All My Cruises hook hydrates from an
 * empty Redux state.
 */
export function computeBookingsImagesFromMetadata(
  bookings: AllBooking[],
  genericConfigs: GenericConfigType
): BookingWithImage[] {
  if (!Array.isArray(bookings) || bookings.length === 0) return [];

  const sorted = [...bookings].sort(
    (a, b) => departureSortKey(a.cruiseId) - departureSortKey(b.cruiseId)
  );

  const usedImagesMap = new Map<string, Set<string>>();
  const lastImageRef = { value: '' };
  const pickImage = createImagePicker(usedImagesMap, lastImageRef);

  return sorted.map((booking) => ({
    bookingNumber: booking.number,
    // Pass empty itinCode and no portImages → effectively uses
    // areaImagesConfig[region] → fallbackImagesConfig.
    image: getItineraryImage('', booking.region, pickImage, genericConfigs),
  }));
}

// ---------------------------------------------------------------------------
// Phase 2 — upgrade with itinerary data (and optionally port images)
// ---------------------------------------------------------------------------

/**
 * Upgrades the previously-computed images using `itineraryCode`-keyed config
 * and/or port images once the All My Cruises page has resolved per-booking
 * itineraries (the only page where that fan-out is allowed).
 *
 * Full priority (preserving the new `17e8086d93` logic):
 *   1. `itineraryCodesImagesConfig[itinData.code]`
 *   2. Port images from `getPortImagesFromItineraryData`
 *   3. `areaImagesConfig[region]`
 *   4. `fallbackImagesConfig`
 *
 * `portsData` is optional — pass it when the caller has already fetched the
 * ports CMS fragment; omit it to skip port images and apply only tiers 1/3/4.
 *
 * Pure function with respect to API calls: no network requests are made here.
 * Returns the same array reference when nothing changed so callers can use
 * reference equality to detect a no-op upgrade.
 */
export function upgradeBookingsImagesWithItineraryCodes(
  currentImages: BookingWithImage[],
  bookings: AllBooking[],
  itinerariesByCruiseId: Record<string, ItineraryResponse | undefined>,
  genericConfigs: GenericConfigType,
  portsData?: PortItem[] | null
): BookingWithImage[] {
  if (!Array.isArray(currentImages) || currentImages.length === 0) return currentImages ?? [];

  const bookingByNumber = new Map<string, AllBooking>();
  for (const booking of bookings) {
    bookingByNumber.set(booking.number, booking);
  }

  const usedImagesMap = new Map<string, Set<string>>();
  const lastImageRef = { value: '' };
  const pickImage = createImagePicker(usedImagesMap, lastImageRef);

  let mutated = false;

  const upgraded = currentImages.map((entry) => {
    const booking = bookingByNumber.get(entry.bookingNumber);
    if (!booking) return entry;

    const itinerary = itinerariesByCruiseId[booking.cruiseId];
    if (!itinerary) return entry;

    const portImages =
      portsData != null ? getPortImagesFromItineraryData(portsData, itinerary) : undefined;

    const upgradedImage = enrichImageAltFromPortImages(
      getItineraryImage(itinerary.code, booking.region, pickImage, genericConfigs, portImages),
      portsData,
      itinerary
    );
    if (!upgradedImage) return entry;

    const upgradedUrl = getImageUrl(upgradedImage);
    const currentUrl = getImageUrl(entry.image);
    if (upgradedUrl === currentUrl && upgradedImage.altText === entry.image?.altText) {
      return entry;
    }

    mutated = true;
    return { ...entry, image: upgradedImage };
  });

  return mutated ? upgraded : currentImages;
}

// ---------------------------------------------------------------------------
// Legacy entry point (kept for reference; no longer has active call sites)
// ---------------------------------------------------------------------------

// get booking data for market and generate array of {bookingNumber, assignedImage}
export async function assignImageToBookingInRotation(genericConfigs: GenericConfigType) {
  const { email } = getActiveSession();

  if (email) {
    try {
      const getBooks = new GetAllBookingsMyMSC(false, { email: email });
      const getBooksData = await getBooks.getData();

      getBooksData.bookings = getBooksData.bookings.filter(
        (booking) => booking.marketCode === genericConfigs.configs.b2cCommerceConfig.countryCodeISO3
      );

      if (getBooksData.bookings.length <= 0) return;
      const bookingsWithImages = await assignImageToBooking(getBooksData, genericConfigs);

      return bookingsWithImages;
    } catch (error) {
      console.error(error);
    }
  }
}

// assign to each booking an image
// by rotating images
// we return an array like {bookingnumber, assignedImage}
async function assignImageToBooking(
  bookData: GetAllBookingsMyMscResponse,
  genericConfigs: GenericConfigType
) {
  const usedImagesMap = new Map<string, Set<string>>();
  let globalLastImageUsed = '';
  const results = [];

  // Fetch all itineraries first
  const bookingsWithItin = await Promise.all(
    bookData.bookings.map(async (booking) => {
      const getItin = new GetItineraryMyMSC(false, { cruiseId: booking.cruiseId });
      const itinData = await getItin.getData();
      return { booking, itinData };
    })
  );

  // Sort by departure date before assigning images
  bookingsWithItin.sort(
    (a, b) =>
      new Date(a.itinData.departureDate).getTime() - new Date(b.itinData.departureDate).getTime()
  );

  const pickImage = (images: ImageConfig[]) => {
    const key = getImageUrl(images[0]);

    if (!usedImagesMap.has(key)) {
      usedImagesMap.set(key, new Set<string>());
    }
    const usedImagesForKey = usedImagesMap.get(key) as Set<string>;
    const imageResults = pickItImageAvoidingDupl(images, usedImagesForKey, globalLastImageUsed);
    if (imageResults) {
      globalLastImageUsed = imageResults.lastImageUsed;
    }
    return imageResults?.selected ?? images[0];
  };

  const needsPortImages = bookingsWithItin.some(({ itinData }) => {
    const itineraryCodeImagesConfig =
      genericConfigs?.configs?.cruiseSearchConfig?.itineraryImages?.itineraryCodesImagesConfig?.[
        itinData.code
      ];
    return !itineraryCodeImagesConfig?.length;
  });

  let portsData: PortItem[] | null = null;
  if (needsPortImages) {
    try {
      const result = await fetchContentFragmentsFromCms<
        IFetchPortsImagesFromFragmentsResponse,
        PortsImagesListGraphQlQueryRequest
      >('getPortsImagesList');
      portsData = result?.data?.ports?.items || [];
    } catch (error) {
      console.error('Error fetching port images:', error);
    }
  }

  for (const { booking, itinData } of bookingsWithItin) {
    const portImages = getPortImagesFromItineraryData(portsData, itinData);
    const image = getItineraryImage(
      itinData.code,
      booking.region,
      pickImage,
      genericConfigs,
      portImages
    );
    results.push({ bookingNumber: booking.number, image });
  }

  return results;
}
